"use client";

import { useTranslations } from "next-intl";
import { useEffect, useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { toast } from "sonner";
import { Form } from "@/components/forms";
import { Button, Input, Label } from "@/components/ui";
import type useLogin from "@/hooks/auth/useLogin";
import { SecondFactorApiError } from "@/lib/auth-password-second-factor";
import {
  makePasswordSecondFactorSchema,
  type PasswordSecondFactorValues,
} from "@/lib/validations/auth";

type LoginMutation = ReturnType<typeof useLogin>;

interface PasswordSecondFactorFormProps {
  login: LoginMutation;
  onCancel?: () => void;
}

function PasswordSecondFactorFields({
  login,
  onCancel,
}: PasswordSecondFactorFormProps) {
  const t = useTranslations("auth");
  const {
    control,
    clearErrors,
    setError,
    formState: { errors, isValid },
  } = useFormContext<PasswordSecondFactorValues>();

  const secondFactor = login.completeSecondFactor;
  const error = secondFactor.error;

  useEffect(() => {
    if (!error) return;

    const code = error instanceof SecondFactorApiError ? error.code : null;

    if (code === "invalid_second_factor_challenge") {
      // The signed challenge expired (~5 min) or is otherwise invalid. Retrying
      // with the same token can never succeed, so drop back to the sign-in step
      // for a fresh challenge instead of trapping the user on "wrong password".
      toast.error(t("login.secondFactorExpired"));
      login.clearSecondFactorChallenge();
      return;
    }

    if (code === "invalid_second_factor_password") {
      setError("password", {
        type: "server",
        message: t("login.passwordInvalid"),
      });
      return;
    }

    // Network / unexpected failure — don't mislabel it as a wrong password.
    toast.error(t("login.secondFactorError"));
    // Deps intentionally exclude `login`: its identity changes every render, and
    // this effect must fire only when the mutation error changes — otherwise
    // setError -> re-render -> effect would loop.
  }, [error, setError, t]);

  const handleCancel = () => {
    login.clearSecondFactorChallenge();
    onCancel?.();
  };

  return (
    <>
      <Controller
        name="password"
        control={control}
        render={({ field }) => (
          <div className="space-y-2 w-full">
            <Label htmlFor="second-factor-password">
              {t("login.secondFactorPasswordLabel")}
            </Label>
            <Input
              {...field}
              id="second-factor-password"
              type="password"
              autoComplete="current-password"
              className={errors.password ? "border-semantic-error" : undefined}
              onChange={(event) => {
                field.onChange(event);
                clearErrors("password");
                if (secondFactor.isError) secondFactor.reset();
              }}
            />
            {errors.password?.message ? (
              <p className="text-xs leading-tight text-semantic-error ms-1.5">
                {errors.password.message}
              </p>
            ) : null}
          </div>
        )}
      />

      <div className="flex flex-row-reverse pb-5 gap-3">
        <Button
          loading={secondFactor.isPending}
          disabled={!isValid || secondFactor.isPending}
          type="submit"
          className="w-full"
          variant="solid"
          colors="secondary"
        >
          {t("login.secondFactorSubmit")}
        </Button>

        <Button
          type="button"
          className="w-full"
          variant="bordered"
          colors="secondary"
          onClick={handleCancel}
        >
          {t("login.cancel")}
        </Button>
      </div>
    </>
  );
}

export function PasswordSecondFactorForm({
  login,
  onCancel,
}: PasswordSecondFactorFormProps) {
  const vt = useTranslations("validation");
  const schema = useMemo(() => makePasswordSecondFactorSchema(vt), [vt]);

  const handleSubmit = async (values: PasswordSecondFactorValues) => {
    try {
      await login.completeSecondFactor.mutateAsync(values);
    } catch {
      // The mutation error state is mapped to the password field above.
    }
  };

  return (
    <Form
      schema={schema}
      defaultValues={{ password: "" }}
      mode="onChange"
      onSubmit={handleSubmit}
    >
      <PasswordSecondFactorFields login={login} onCancel={onCancel} />
    </Form>
  );
}
